import { api } from './api';

export interface TrendItem {
  query: string;
  search_volume: number | null;
  increase_percentage: number | null;
  active: boolean;
  start_timestamp: number | null;
  trend_breakdown: string[];
  serpapi_news_link: string | null;
  // Coverage Score
  score: number;
  band: 'alta' | 'media' | 'baixa' | string;
  editoria: string | null;
  audiencia: number;
  momentum: number;
  fit: number;
  confianca: number;
  flags: string[];
}

export interface TrendsResponse {
  items: TrendItem[];
  geo: string;
  hours: number;
  total: number;
}

export interface NewsCard {
  title: string;
  source: string | null;
  source_icon: string | null;
  link: string | null;
  date: string | null;
  thumbnail: string | null;
}

export interface TopicDetail {
  query: string;
  score: number;
  band: string;
  editoria: string | null;
  verdict: 'FURO' | 'COBRIR' | 'SATURADO' | 'IGNORAR' | string;
  motivo: string;
  confianca: number;
  flags: string[];
  num_sources: number;
  news: NewsCard[];
  paa: string[];
  keywords: string[];
  trend_breakdown: string[];
  search_volume: number | null;
  increase_percentage: number | null;
}

export interface PautaResponse {
  id: number;
  tema: string;
  fonte: string;
  estado: string;
  nivel_autonomia: string;
  created_at: string;
  dados_origem?: { editoria?: string; score?: number; band?: string; origem?: string } | null;
}

export interface TrendFilters {
  geo?: string;
  hours?: number;
  category?: string;
  onlyActive?: boolean;
}

export const intelligenceService = {
  getTrends: (f: TrendFilters = {}): Promise<TrendsResponse> => {
    const qs = new URLSearchParams();
    qs.set('geo', f.geo ?? 'BR');
    qs.set('hours', String(f.hours ?? 24));
    if (f.category) qs.set('category', f.category);
    if (f.onlyActive) qs.set('only_active', 'true');
    return api.get<TrendsResponse>(`/intelligence/trends?${qs}`);
  },
  getTopic: (item: {
    query: string;
    search_volume?: number | null;
    increase_percentage?: number | null;
    editoria?: string | null;
  }): Promise<TopicDetail> => {
    const qs = new URLSearchParams();
    qs.set('query', item.query);
    if (item.search_volume != null) qs.set('search_volume', String(item.search_volume));
    if (item.increase_percentage != null)
      qs.set('increase_percentage', String(item.increase_percentage));
    if (item.editoria) qs.set('editoria', item.editoria);
    return api.get<TopicDetail>(`/intelligence/topic?${qs}`);
  },
  listPautas: (): Promise<PautaResponse[]> =>
    api.get<PautaResponse[]>('/intelligence/pautas'),

  // ─── Fluxo de Quentes (esteira automática) ───────────────────────────────
  getSerpProvider: (): Promise<{ provider: string }> =>
    api.get<{ provider: string }>('/intelligence/serp-provider'),
  setSerpProvider: (provider: string): Promise<{ provider: string }> =>
    api.put<{ provider: string }>('/intelligence/serp-provider', { provider }),

  getQuentesConfig: (): Promise<QuentesConfig> =>
    api.get<QuentesConfig>('/intelligence/quentes/config'),
  saveQuentesConfig: (cfg: QuentesConfig): Promise<QuentesConfig> =>
    api.put<QuentesConfig>('/intelligence/quentes/config', cfg),
  runQuentes: (geo?: string): Promise<QuentesRunResult> =>
    api.post<QuentesRunResult>(
      `/intelligence/quentes/run?force=true${geo ? `&geo=${encodeURIComponent(geo)}` : ''}`,
      {},
    ),

  // ─── Esteira de Trends (fricção off | manual | auto) ─────────────────────
  getEsteiraConfig: (): Promise<EsteiraConfig> =>
    api.get<EsteiraConfig>('/intelligence/esteira/config'),
  saveEsteiraConfig: (cfg: Partial<EsteiraConfig>): Promise<EsteiraConfig> =>
    api.put<EsteiraConfig>('/intelligence/esteira/config', cfg),
  runEsteira: (): Promise<EsteiraRunResult> =>
    api.post<EsteiraRunResult>('/intelligence/esteira/run', {}),
};

export type EsteiraMode = 'off' | 'manual' | 'auto';

export interface EsteiraConfig {
  mode: EsteiraMode;
  geo: string;
  hours: number;
  min_score: number;
  max_per_cycle: number;
  intervalo_horas: number;
  wp_site: string | null;
  status: 'draft' | 'publish' | string;
  engine: 'prod' | 'v2' | string;
  kill_switch: boolean;
}

export interface EsteiraRunResult {
  mode?: string;
  candidatos?: number;
  n?: number;
  produced?: {
    query: string;
    score?: number;
    editoria?: string;
    wp_id?: number;
    wp_link?: string;
    cost_usd?: number;
  }[];
  skipped?: string;
}

export interface QuentesEditoria {
  enabled: boolean;
  cap_dia: number;
  wp_category_id: number | null;
}

export interface QuentesConfig {
  enabled: boolean;
  geo: string;
  hours: number;
  score_cut: number;
  min_sources: number;
  publish_status: 'draft' | 'publish' | string;
  active_start: number;
  active_end: number;
  max_per_cycle: number;
  kill_switch: boolean;
  editorias: Record<string, QuentesEditoria>;
}

export interface QuentesProduced {
  tema: string;
  editoria: string;
  score: number;
  wp_id: number;
  wp_link: string;
  status: string;
  sources: number;
}

export interface QuentesRunResult {
  ranked?: number;
  picked?: number;
  produced?: QuentesProduced[];
  skipped?: { tema: string; motivo: string }[];
  publish_status?: string;
}
